// Pre-fill the reply compose modal with "Hi <FirstName>," plus an
// optional canned response to a recognised pleasantry in the original
// email ("How are you?" → "I'm good thanks, how are you?", etc.).
// Saves the user from re-typing the same opener every reply.
//
// Pure module — no React, no DOM. Returns a plain-text string with
// `\n` newlines; ComposeModal's buildBodyHtml() escapes and converts
// newlines to <br> on render. Easy to unit-test, easy to swap for
// something LLM-driven later.

// First names that aren't really names — extracted from generic role
// inboxes (info@acme.com → "info"). Skip personalisation for these
// rather than addressing the recipient as "Hi Info,". Falls back to a
// bare "Hi," which still reads naturally.
const GENERIC_LOCAL_PARTS = new Set([
  'info', 'sales', 'admin', 'support', 'hello', 'hi', 'contact', 'enquiries',
  'enquiry', 'noreply', 'no-reply', 'do-not-reply', 'donotreply', 'office',
  'team', 'orders', 'accounts', 'billing', 'finance', 'careers', 'jobs',
  'marketing', 'reception', 'mail', 'mailer', 'postmaster', 'admin',
  // Role inboxes common in our customers' setups — these are usually
  // staffed by a named person who signs off in the body, so when the
  // sender's local-part is one of these, the caller should fall
  // through to the signature scan (extractSignatureFirstName) before
  // landing on a bare "Hi,".
  'artwork', 'production', 'design', 'studio', 'creative', 'graphics',
  'purchasing', 'purchase', 'shipping', 'dispatch', 'warehouse', 'returns',
]);

// Pleasantry detection. Each entry has a regex to match against the
// (lowercased, head-of-body) original message and a `response` —
// either a string or a function that receives the regex match and
// returns the response. Order matters: more specific patterns first
// (e.g. "had a good weekend" before bare "weekend").
//
// Responses are deliberately conversational rather than corporate —
// the user wanted the reply to *sound* like their own typing, not
// auto-generated text. The user can still edit before sending.
//
// The wording people actually use is looser than the first cut of these
// allowed. Mined from the opening lines of 6,488 first-contact emails received
// in the 60 days to 2026-10-08: "hope your well" (no apostrophe), "hope you're
// keeping / doing well", "hope you're good / ok", "hope this email finds you
// well", "hope all is well with you", "trust you are well", "hope you had a
// nice / lovely weekend", "how's things?". Each pattern below is written to
// take those in.

// "you are" as people type it: you are / you're / youre / your / ur / u r / u.
const YOU_ARE = String.raw`(?:you\s+(?:are|r)|you'?re|your|u\s*'?r(?:e)?|ur|you|u)`;
const WHO = String.raw`(?:\s+(?:both|all|guys|two))?`;
const GOOD = String.raw`(good|great|nice|lovely|relaxing|restful|fab|fabulous|fantastic|wonderful|amazing|brilliant|super)`;
const HOPE = String.raw`(?:hope|hoping|trust)\s+(?:that\s+)?`;

const PLEASANTRY_PATTERNS = [
  // Specific time-of-year / event echoes
  {
    pattern: new RegExp(String.raw`\b${HOPE}you${WHO}(?:'ve|\s+have)?\s+had\s+an?\s+${GOOD}\s+(?:long\s+)?weekends?\b`, 'i'),
    response: 'It was good thanks, how was yours?',
  },
  {
    pattern: /\bhow\s+was\s+(?:your|the)\s+weekend\b/i,
    response: 'It was good thanks, how was yours?',
  },
  {
    pattern: new RegExp(String.raw`\b${HOPE}you${WHO}(?:'ve|\s+have)?\s+had\s+an?\s+${GOOD}\s+(christmas|easter|holiday|break|new\s+year|bank\s+holiday|time\s+off|trip|evening|night)\b`, 'i'),
    response: m => `It was ${m[1].toLowerCase()} thanks, hope yours was too.`,
  },
  {
    pattern: new RegExp(String.raw`\b${HOPE}${YOU_ARE}${WHO}\s+having\s+an?\s+${GOOD}\s+(day|week|morning|afternoon|evening|monday|tuesday|wednesday|thursday|friday)\b`, 'i'),
    response: m => `Thanks, you too — having a ${m[1].toLowerCase()} ${m[2].toLowerCase()} here.`,
  },
  // Generic well-wishes — before "how are you" so a combined
  // "hope you're well, how are you?" lands on the warmer of the two.
  {
    pattern: new RegExp(String.raw`\b${HOPE}(?:you\s+)?(?:all|everything|things|everyone)(?:\s+(?:is|are)|'s)?\s+(?:going\s+)?(?:well|good|ok|okay|fine|great)\b`, 'i'),
    response: 'All well thanks, hope same with you.',
  },
  {
    pattern: new RegExp(String.raw`\b${HOPE}(?:this\s+(?:(?:email|message|e-mail|note)\s+)?)?finds\s+you${WHO}\s+(?:very\s+)?well\b`, 'i'),
    response: 'I\'m well thanks, hope you are too.',
  },
  {
    pattern: new RegExp(String.raw`\b${HOPE}${YOU_ARE}${WHO}(?:\s+(?:keeping|doing|staying|feeling))?\s+(?:very\s+|really\s+|all\s+)?(?:well|good|ok|okay|fine|safe|great)\b`, 'i'),
    response: 'I\'m well thanks, hope you are too.',
  },
  // "Hope you and the family are keeping well", "hope you and Peggy are well".
  {
    pattern: new RegExp(String.raw`\b${HOPE}you\s+and\s+[^.!?\n]{2,40}?\s+(?:are|r)\s+(?:all\s+)?(?:keeping\s+|doing\s+)?(?:well|good|ok|okay|fine)\b`, 'i'),
    response: 'All well thanks, hope same with you.',
  },
  // "Hope your day / week is going well".
  {
    pattern: /\b(?:hope|hoping|trust)\s+(?:that\s+)?your\s+(day|week|morning|afternoon|monday|tuesday|wednesday|thursday|friday)\s+is\s+going\s+(?:well|good|ok|okay|great)\b/i,
    response: m => `Going well thanks, hope your ${m[1].toLowerCase()} is too.`,
  },
  // How are you / how's things family.
  {
    pattern: /\bhow(?:'s|\s+is|\s+are)\s+(?:things|everything|it\s+going|life|tricks|business)\b/i,
    response: 'All good thanks, how are things with you?',
  },
  {
    pattern: /\bhow(?:\s+are|'re|\s+r)?\s+(?:you|u|ya)(?:\s+(?:doing|keeping|today|getting\s+on))?\s*\?/i,
    response: 'I\'m good thanks, how are you?',
  },
  {
    pattern: /\bhow(?:\s+are|'re)\s+you(?:\s+doing)?\b/i,
    response: 'I\'m good thanks, how are you?',
  },
  // Day-of-week / season echoes — capture the day so we can echo it
  // back. Capitalise on return so "happy friday" → "Happy Friday!".
  {
    pattern: /\bhappy\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekend|new\s+year|birthday|anniversary|christmas|easter|halloween)\b/i,
    // Tied to the day, so it can be echoed on a thread too (see threadMovedOnADay).
    dated: true,
    response: m => {
      const day = m[1].split(/\s+/).map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      return `Happy ${day}!`;
    },
  },
  {
    pattern: /\bgood\s+morning\b/i,
    response: 'Good morning!',
  },
  {
    pattern: /\bgood\s+afternoon\b/i,
    response: 'Good afternoon!',
  },
];

// The user's own phrase → response pairs (saved from the dashboard, per
// mailbox). A phrase matches when its words appear, in order, in the head of
// the email — ignoring case, punctuation and how apostrophes are typed.
const normPhrase = (t) => String(t || '')
  .toLowerCase()
  .replace(/[‘’ʼ‛]/g, '\'')
  .replace(/[^a-z0-9'\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
function matchCustomOpener(head, customOpeners) {
  if (!Array.isArray(customOpeners) || !customOpeners.length) return null;
  const text = ` ${normPhrase(head)} `;
  // Longest phrase first, so "hope you had a good holiday in spain" beats
  // "hope you had a good holiday".
  const sorted = [...customOpeners].sort((a, b) => String(b?.phrase || '').length - String(a?.phrase || '').length);
  for (const o of sorted) {
    const phrase = normPhrase(o?.phrase);
    const response = String(o?.response || '').trim();
    if (phrase.length < 3 || !response) continue;
    if (text.includes(` ${phrase} `)) return response;
  }
  return null;
}

// Strip HTML to plain text. Just enough to give the pleasantry regex
// something to match against — don't need to render anything. Cap at
// 1200 chars so we only look at the visible top of the email, not a
// long quoted history.
function stripHtmlForScan(s) {
  if (!s) return '';
  return String(s)
    // Drop quoted thread blocks (Outlook + Gmail style) before stripping
    // tags — a "How are you?" buried in a reply chain shouldn't trigger.
    .replace(/<blockquote[^>]*>[\s\S]*$/i, ' ')
    .replace(/<div[^>]*data-compose-quote[^>]*>[\s\S]*$/i, ' ')
    // Outlook reply boundary — a horizontal rule immediately before the
    // From/Sent/To/Subject header block. Strip from the first <hr> to
    // end-of-body since Outlook puts nothing meaningful after the rule.
    // Bug it fixes: the previous strip set missed the Phil/Indigo
    // reply layout (no <blockquote>, no "On wrote") and the signature
    // scanner walked deep into quoted Darren content.
    .replace(/<hr[^>]*>[\s\S]*$/i, ' ')
    // "On <date>, X wrote:" style headers — chop everything after.
    .replace(/On\s+[^<]+wrote:[\s\S]*$/i, ' ')
    // Bare-tag strip
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/?(br|p|div|tr|li|h[1-6])[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, '\'')
    // Smart/curly apostrophe → ASCII. Outlook and Word auto-convert
    // straight apostrophes to U+2019 (’) as the user types, so
    // "I hope you're well" arrives in the bot as "I hope you’re well"
    // and the regex /'re/ misses it. Bug report: user typed
    // "I hope you're well", saw greeting-only fallback because of
    // exactly this. Normalise ALL Unicode apostrophe variants AND
    // their HTML entity forms to ASCII here so the downstream regex
    // only has to handle one character.
    .replace(/[‘’ʼ‛]/g, '\'')
    .replace(/&rsquo;/gi, '\'')
    .replace(/&lsquo;/gi, '\'')
    .replace(/&#(?:8216|8217|8219);/g, '\'')
    // Outlook plain-text reply header — "From: <name> <email>" on a
    // line of its own. By this point the HTML tags have been stripped,
    // so we're looking at the post-strip text. The angle-bracketed
    // email is the strong signal that this is a reply header, not the
    // word "From:" in normal prose. Chop from the From line to end.
    .replace(/\n\s*From:\s+[^\n]*<[^>]+@[^>]+>[\s\S]*$/i, ' ')
    // Second-chance Outlook header chop — when the input was PLAIN
    // TEXT (no HTML), the bare <email@…> in the From line above
    // gets eaten by the earlier `<[^>]+>` tag-strip, so the angle-
    // bracket pattern fails to fire. Catch it via the header
    // signature instead: "From: …" followed within a few lines by
    // any of "Sent:" / "Date:" / "To:" / "Subject:". This is
    // distinctively Outlook's reply-header layout and never appears
    // in legitimate prose.
    .replace(/\n\s*From:\s+[^\n]+\n+\s*(?:Sent|Date|To|Subject):[\s\S]*$/i, ' ')
    .replace(/[ \t]+/g, ' ')
    .slice(0, 1200);
}

// Capitalise just the first letter — preserve mixed case (e.g.
// "McKenzie" stays "McKenzie", not "Mckenzie").
function capFirst(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Extract a first name from a sender's display name or email address.
 * Returns '' when the result would be a generic role inbox alias.
 *
 *   "Jenny Higgins"           → "Jenny"
 *   "Jenny Higgins <j@x.com>" → "Jenny"
 *   "j.higgins@formkraft.co.uk" → "J"  (1-char local-parts skip)
 *   "jenny.higgins@x.com"     → "Jenny"
 *   "info@acme.com"           → ""    (generic)
 *   ""                        → ""
 */
function extractFirstName(displayName, email) {
  // Display name first — it's user-friendlier than a local-part guess.
  const dn = String(displayName || '').trim();
  if (dn && !dn.includes('@')) {
    // Strip a trailing " <email@…>" pair if present.
    const cleaned = dn.replace(/<[^>]+>/g, '').trim();
    if (cleaned) {
      const first = cleaned.split(/\s+/)[0];
      // Some senders prefix "Mr ", "Mrs ", "Dr " — skip those if we
      // have a second word.
      const honorifics = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'prof']);
      if (honorifics.has(first.toLowerCase().replace(/\.$/, ''))) {
        // Honorific + given name + surname: take the given name
        // ("Mr John Smith" → "John"). Honorific + ONE more word
        // (typically a surname-only display) → fall back to ''
        // and let the caller emit a bare "Hi,". Returning the
        // surname directly ("Mr Smith" → "Hi Smith,") reads as
        // overly-familiar UK business etiquette violation.
        const parts = cleaned.split(/\s+/);
        if (parts.length >= 3) {
          return capFirst(parts[1]);
        }
        return '';
      }
      // Skip if it's clearly an email address that landed in the
      // display-name slot.
      if (first.includes('@') || first.includes('.')) {
        // Fall through to email-based extraction.
      } else if (GENERIC_LOCAL_PARTS.has(first.toLowerCase())) {
        // Display name is a role label ("Artwork", "Production",
        // "Sales") — don't address them by it. Fall through so the
        // caller's signature-scan path can find the real signer in
        // the body. "Hi Artwork," reads as comically wrong; better
        // to use the body's "Kind regards, Phil" than the inbox label.
      } else {
        return capFirst(first);
      }
    }
  }
  // Email local-part: jenny.higgins@x.com → jenny
  const addr = String(email || displayName || '').toLowerCase();
  const local = addr.split('@')[0].replace(/[<>]/g, '').trim();
  if (!local) return '';
  if (GENERIC_LOCAL_PARTS.has(local)) return '';
  // Split on . _ - and take first segment
  const first = local.split(/[._-]/)[0];
  if (!first) return '';
  // Single-letter local parts (e.g. "j" from "j.higgins") aren't worth
  // addressing by — "Hi J," reads odd. Fall back to a bare "Hi,".
  if (first.length < 2) return '';
  // Also skip if the first segment alone is a generic role.
  if (GENERIC_LOCAL_PARTS.has(first)) return '';
  return capFirst(first);
}

// Common email sign-offs. Listed roughly longest-first so the regex
// engine prefers the more-specific match — "kind regards" lands on
// the full phrase rather than just "regards" + a stray "kind". Add
// new variants as we see them in real customer mail.
const SIGNOFF_PHRASES = [
  'kind regards', 'kindest regards', 'warm regards', 'warmest regards',
  'best regards', 'best wishes', 'all the best', 'many thanks',
  'thanks a lot', 'thank you', 'thanks again', 'thanks so much',
  'yours sincerely', 'yours faithfully', 'yours truly',
  'sincerely yours', 'sincerely',
  'cheers', 'regards', 'thanks', 'best', 'ta',
];

// Words that look like the start of a signature line but aren't a name.
// Keep this list tight — over-blocking strips real first names. Each
// entry should be a word that NEVER appears as someone's first name.
const SIGNATURE_BLOCKLIST = new Set([
  // Sign-off remnants that wrapped onto the next line
  'regards', 'kind', 'kindest', 'warm', 'warmest', 'best', 'cheers',
  'sincerely', 'yours', 'faithfully', 'truly', 'many', 'thanks',
  'thank', 'all', 'ta',
  // Generic team/role markers — "The Team", "From the team", etc.
  'the', 'from', 'team', 'office', 'support', 'sales', 'admin',
  // Sent-by-mobile footers
  'sent', 'get', 'download',
  // Headers / quoted reply boundaries that might land here
  'subject', 'date', 'to', 'cc', 'on', 'wrote',
  // Common opening words of company-line autosignatures
  'attached', 'please', 'hope',
]);

/**
 * Scan the body's signature area for a name. Used as a third-tier
 * fallback after mate-detection and display-name extraction both
 * miss — typical case is a role inbox ("Artwork", "Production")
 * where the email is sent from a generic alias but signed off with
 * a real person's name at the bottom.
 *
 * Algorithm:
 *   1. Strip HTML, take the LAST 1500 chars (signature lives at end)
 *   2. Find a sign-off phrase ("Kind regards", "Thanks", etc.)
 *   3. The first non-empty line after the sign-off is the candidate
 *   4. Validate: first word looks like a name (cap letter, length 2+,
 *      not in SIGNATURE_BLOCKLIST, no @ or . inside it)
 *   5. Return that first word capitalised, or '' on no match
 *
 * Examples (visible body text after sign-off):
 *   "Kind regards\nPhil\nIndigo Design Studio"   → "Phil"
 *   "Thanks,\nSarah"                              → "Sarah"
 *   "Best regards\nJohn Smith\n+44 ..."           → "John"
 *   "Cheers,\nThe Team at Acme"                   → ''   (blocklisted "the")
 *   "Regards,\nartwork@indigo..."                 → ''   ("@" in candidate)
 *   no sign-off in text                          → ''
 */
function extractSignatureFirstName(bodyHtmlOrText) {
  if (!bodyHtmlOrText) return '';
  // CRITICAL: scan only the NEW content of the email, not the
  // quoted history below it. Previously this used a DOM .innerText
  // strip of the WHOLE body + .slice(-1500). For replies, Outlook
  // embeds the prior thread underneath the sender's new content —
  // so the tail of the body was deep in the QUOTED history, and the
  // sender's "Thanks / Phil" sign-off at the top got cut off.
  // Result: matched some random sign-off + name from quoted text
  // (usually our own previous signature), then failed validation,
  // leading to the bug "From Artwork still shows Hi, despite the
  // body clearly being signed off by Phil".
  //
  // stripHtmlForScan already chops the quoted history at the
  // standard boundaries (<blockquote>, <div data-compose-quote>,
  // "On <date>, X wrote:"). Re-use it so the signature scan sees
  // exactly the same "new content only" view detectPleasantry uses.
  // Cap is 1200 chars — enough for any reasonable opening + sig
  // (typical reply is under 600 chars of new content).
  const text = stripHtmlForScan(bodyHtmlOrText);
  if (!text) return '';
  // No tail-slice — stripHtmlForScan already trimmed to a manageable
  // window AND removed the quoted history. The LAST sign-off in this
  // window is the sender's own sign-off (nothing else after the
  // quoted history was removed). Earlier in the same window might
  // contain "thanks for the quote" prose — the `.exec` loop below
  // still prefers the LAST match, which is the right one.
  const tail = text;

  // Build a single alternation of sign-off phrases. Each is escaped
  // for any regex metachars (none in the current list but defensive).
  const signoffAlt = SIGNOFF_PHRASES
    .map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'))
    .join('|');
  // (?:signoff)[,.! optional]\n+(<capture next non-empty line>)
  // Anchored with \b so "regard" alone doesn't trip the rule.
  const pattern = new RegExp(
    `\\b(?:${signoffAlt})\\s*[,.!]?\\s*\\n+\\s*([^\\n]{2,80})`,
    'i'
  );

  // Find ALL matches — the last one wins (latest sign-off in the
  // body, which is more reliably the actual signer rather than a
  // quoted earlier reply). Plain match returns the first; we want
  // the last, so use matchAll with the /g flag.
  const allRe = new RegExp(pattern.source, 'gi');
  let lastMatch = null;
  let m;
  while ((m = allRe.exec(tail)) !== null) {
    lastMatch = m;
    if (allRe.lastIndex === m.index) allRe.lastIndex++;   // safety against zero-width
  }
  if (!lastMatch) return '';

  const candidateLine = lastMatch[1].trim();
  if (!candidateLine) return '';

  // Take the first word as the name candidate.
  const firstWord = candidateLine.split(/\s+/)[0];
  if (!firstWord || firstWord.length < 2) return '';
  if (firstWord.includes('@') || firstWord.includes('.')) return '';

  // Lowercase for blocklist check. Original case preserved for return.
  const lower = firstWord.toLowerCase().replace(/[,.!]$/, '');
  if (SIGNATURE_BLOCKLIST.has(lower)) return '';
  // Reject if it's a generic role alias.
  if (GENERIC_LOCAL_PARTS.has(lower)) return '';

  // Validate: letters / hyphen / apostrophe only, starts with a capital
  // (most signatures do). "Mary-Jane" and "O'Brien" pass.
  if (!/^[A-Z][A-Za-z'\-]+$/.test(firstWord.replace(/[,.!]$/, ''))) return '';

  return capFirst(firstWord.replace(/[,.!]$/, ''));
}

// Salutation overrides that should win over the sender's real first
// name. Matched against the TOP of the body only (first ~80 chars after
// stripping) so a "mate" buried deep inside a quoted thread doesn't
// flip the register on every reply. Pattern shape:
//   /^\s*(?:hi|hey|hello)?\s*<TOKEN>\b/i
// — optional leading greeting word, then the salutation token. Anchored
// to the start of the body so we only fire on the actual opening line.
//
// Extend by adding more (pattern, replacement) pairs. Each replacement
// fills in for what would otherwise be the sender's first name.
const SALUTATION_OVERRIDES = [
  // "Hi mate", "Morning mate", "Afternoon mate", "Alright mate", "Cheers mate" …
  { pattern: /^\s*(?:hi|hey|hello|hiya|yo|(?:good\s+)?(?:morning|afternoon|evening)|alright|cheers|thanks|ta)\s+mate\b/i, replacement: 'mate' },
  { pattern: /^\s*mate\s*[,!.]/i,                       replacement: 'mate' },
];

/**
 * If the original message opens with a recognised informal salutation
 * ("Hi mate", "Hey mate", bare "Mate,"), return the matching token so
 * the reply mirrors it. Returns null when nothing matches and the
 * caller should fall back to the sender's real first name.
 *
 * Scans only the first ~80 chars of the stripped body — the opening
 * line of the email — so a "mate" in the middle of someone's prose
 * doesn't accidentally re-register the whole conversation.
 */
function detectMateSalutation(bodyHtmlOrText) {
  const head = stripHtmlForScan(bodyHtmlOrText).trimStart().slice(0, 80);
  if (!head) return null;
  for (const { pattern, replacement } of SALUTATION_OVERRIDES) {
    if (pattern.test(head)) return replacement;
  }
  return null;
}

/**
 * Scan the head of the original message for a recognised pleasantry
 * and return the canned response, or null if nothing matches.
 *
 * The body argument can be HTML or plain text — we strip aggressively
 * before scanning.
 */
function detectPleasantry(bodyHtmlOrText, customOpeners) {
  const head = stripHtmlForScan(bodyHtmlOrText).toLowerCase();
  if (!head) return null;
  // The user's own saved responses win over the built-in ones.
  const custom = matchCustomOpener(head, customOpeners);
  if (custom) return custom;
  for (const { pattern, response } of PLEASANTRY_PATTERNS) {
    const m = head.match(pattern);
    if (m) return typeof response === 'function' ? response(m) : response;
  }
  return null;
}

// A "Re:"-prefixed subject means the message is already part of an ongoing
// back-and-forth — greetings + pleasantries were exchanged on the opening
// email, so the reply opener should be just "Hi <Name>," with no echoed
// "I'm well thanks, hope you are too." line. (Fwd: is deliberately NOT treated
// as ongoing — a forward can start a fresh conversation.)
function isReplySubject(subject) {
  return /^\s*re\s*:/i.test(String(subject || ''));
}

// Does the message body already carry a quoted prior message (i.e. it's a
// thread reply, not a fresh first email)? Catches ongoing threads whose
// subject somehow lost its "Re:" prefix. Uses the same unambiguous
// reply-boundary markers stripHtmlForScan chops on.
function hasQuotedHistory(bodyHtmlOrText) {
  const s = String(bodyHtmlOrText || '');
  return /<blockquote/i.test(s)
      || /data-compose-quote/i.test(s)
      || /\bOn\b[^<\n]{0,200}?\bwrote:/i.test(s);
}

// True when this message is part of an ongoing thread — used to suppress the
// echoed pleasantry so the opener stays a bare "Hi <Name>,". A bare greeting is
// never wrong, so we lean toward suppression (either signal is enough).
function isOngoingThread(msg) {
  return isReplySubject(msg?.subject)
      || hasQuotedHistory(msg?.bodyHtml || msg?.bodyText || msg?.bodyPreview || '');
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// The day a quoted header's date falls on, as YYYY-M-D, or null if there is no
// date to read. Numbers-only dates are read day first, as this app and UK Outlook
// write them.
function dayInHeader(text) {
  const key = (y, m, d) => (m >= 1 && m <= 12 && d >= 1 && d <= 31 ? `${y}-${m}-${d}` : null);
  const month = (name) => MONTHS.indexOf(String(name).slice(0, 3).toLowerCase()) + 1;
  let m = text.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  if (m) return key(+m[1], +m[2], +m[3]);
  m = text.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (m) return key(+m[3], +m[2], +m[1]);
  m = text.match(/\b(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]{3,9})\.?,?\s+(\d{4})\b/i);
  if (m && month(m[2])) return key(+m[3], month(m[2]), +m[1]);
  m = text.match(/\b([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\b/i);
  if (m && month(m[1])) return key(+m[3], month(m[1]), +m[2]);
  return null;
}

// True when this email is on a later day than the one before it in the thread,
// read from the first quoted header in its body ("Sent: 10 September 2026",
// "On Thu, 10 Sept 2026 at 11:16, … wrote:"). That is when "Happy Friday" is worth
// echoing on a thread: Cara's "Re:" picked an old enquiry up again seven weeks on
// and got a bare "Hi Cara," (2026-10-30), while a same-day back-and-forth stays
// quiet. No readable date is false: a bare greeting is never wrong.
function threadMovedOnADay(msg) {
  const when = new Date(msg?.date || '');
  if (Number.isNaN(when.getTime())) return false;
  const text = String(msg?.bodyHtml || msg?.bodyText || '')
    .replace(/<(?:style|script)\b[\s\S]*?<\/(?:style|script)>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ');
  const header = /\b(?:sent|date)\s*:\s*|\bon\s+(?=.{0,160}?\bwrote\s*:)/gi;
  let hit;
  while ((hit = header.exec(text))) {
    const day = dayInHeader(text.slice(hit.index + hit[0].length, hit.index + hit[0].length + 60));
    if (day) return day !== `${when.getFullYear()}-${when.getMonth() + 1}-${when.getDate()}`;
  }
  return false;
}

// Only the day-tied pleasantries, for an email on an ongoing thread.
function detectDatedPleasantry(bodyHtmlOrText) {
  const head = stripHtmlForScan(bodyHtmlOrText).toLowerCase();
  for (const { pattern, response, dated } of PLEASANTRY_PATTERNS) {
    if (!dated) continue;
    const m = head.match(pattern);
    if (m) return response(m);
  }
  return null;
}

// Escape user-provided strings before they go into HTML output. Only
// needed for the greeting + pleasantry response — both come from
// patterns we control, but better to defend now than discover a name
// with an unescaped `<` later.
function escHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build the prefilled body for a reply compose, including a wrapping
 * style div and (optionally) the quoted history. Returns an HTML
 * string suitable for direct assignment to a contentEditable's
 * innerHTML.
 *
 * Each line is its own block <div> rather than `text<br><br>` because
 * Chrome / WebKit silently drop a trailing <br> at the end of a block
 * element, so an opener like `Hi Dorothy,<br><br>` rendered as ONE
 * line break visible, not two — the cursor landed on line 2 (right
 * under the greeting) instead of line 3 (a blank line below it).
 * Block divs always render their own line, even when empty (with
 * an internal <br>).
 *
 * The last paragraph carries data-reply-cursor so the autofocus
 * in ComposeModal can position the cursor INSIDE it (not just at
 * the end of the wrapping div, which is ambiguous about which line
 * the cursor sits on).
 *
 * Pass quoteHtml as the second arg to include the quoted thread
 * underneath the opener; it gets wrapped in the standard
 * data-compose-quote div so the existing patchInlineQuoteWhenReady
 * logic keeps working.
 *
 * msg shape: { fromName, from, bodyHtml, bodyText, bodyPreview, … }
 */
function buildReplyOpener(msg, quoteHtml, { selfMailbox = '', customOpeners = [], staffAddresses = null } = {}) {
  const body = msg?.bodyHtml || msg?.bodyText || msg?.bodyPreview || '';
  const self = String(selfMailbox || '').toLowerCase();
  const fromIsSelf = !!self && String(msg?.from || '').toLowerCase() === self;

  let firstName;
  let pleasantry;
  if (fromIsSelf) {
    // Replying to a SENT item: the sender is the mailbox owner, so the
    // from-name path would greet ourselves ("Hi Taylor," to Taylor).
    // Greet the first original recipient (the customer) instead, and
    // skip pleasantry detection entirely — the body text is OUR OWN
    // words, so auto-responding to our own "hope you're well" is wrong.
    const toList = Array.isArray(msg?.toAddresses) ? msg.toAddresses : [];
    const firstOther = toList.find(a => a?.email && String(a.email).toLowerCase() !== self);
    firstName = firstOther ? extractFirstName(firstOther.name, firstOther.email) : '';
    pleasantry = null;
  } else {
    // Three-tier first-name resolution. Earliest match wins.
    //
    //   1. mateOverride — the sender's own salutation is "Hi mate" etc;
    //      mirror the casual register rather than addressing them by name.
    //   2. extractFirstName — display name or email local-part. Returns
    //      '' for role inboxes (artwork@, production@, info@) and bare
    //      first letters ("j.higgins@" → no "Hi J,").
    //   3. extractSignatureFirstName — scan the body's sign-off area
    //      ("Kind regards, Phil") for a real signer. This is what
    //      catches role-inbox mail like artwork@ where the email is
    //      sent from a generic alias but signed by a named person.
    //
    // Falls back to bare "Hi," when all three miss.
    const mateOverride = detectMateSalutation(body);
    firstName =
         mateOverride
      || extractFirstName(msg?.fromName, msg?.from)
      || extractSignatureFirstName(body);
    // Only echo a pleasantry on the FIRST exchange. If this message is already
    // part of an ongoing thread ("Re:" subject, or its body quotes a prior
    // message), the greetings were done on the opening email — just use
    // "Hi <Name>," and don't repeat "I'm well thanks, hope you are too."
    // The exception is a day-tied one ("Happy Friday") once the thread has moved
    // on a day: threadMovedOnADay.
    if (!isOngoingThread(msg)) pleasantry = detectPleasantry(body, customOpeners);
    else pleasantry = threadMovedOnADay(msg) ? detectDatedPleasantry(body) : null;
  }
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  // A reply to a colleague starts empty, ready to type or to paste an email into:
  // staff asked for no "Hi Name," on internal mail, and with none here a pasted
  // "Hi Sam," stays (ComposeModal only drops a pasted greeting when one is already
  // there). Staff only: a supplier on @pinpoint.promo is still greeted. No
  // pleasantry either, which would otherwise sit alone at the top. Without the
  // list (`staffAddresses` not loaded) everyone is greeted, as before.
  const toAddress = fromIsSelf
    ? (Array.isArray(msg?.toAddresses) ? msg.toAddresses : []).find(a => a?.email && String(a.email).toLowerCase() !== self)?.email
    : msg?.from;
  const toStaff = !!staffAddresses && staffAddresses.has(String(toAddress || '').trim().toLowerCase());
  const DEFAULT = 'font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#0f1a33;';

  // Block-paragraph structure. Each <div> is a visible line, even
  // when its only content is <br>. The cursor div sits at the end
  // and gets focused.
  // data-reply-greeting marks the greeting line so a quote paste can recolour
  // it to match the pasted quote (ComposeModal handlePaste + lib/quoteColor).
  const blocks = toStaff ? [] : [`<div data-reply-greeting>${escHtml(greeting)}</div>`, '<div><br></div>'];
  if (pleasantry && !toStaff) {
    // A saved response can run to several lines.
    for (const line of String(pleasantry).split(/\r?\n/)) {
      blocks.push(line.trim() ? `<div>${escHtml(line)}</div>` : '<div><br></div>');
    }
    blocks.push('<div><br></div>');
  }
  blocks.push('<div data-reply-cursor><br></div>');

  const userArea = `<div style="${DEFAULT}margin-bottom:16px;">${blocks.join('')}</div>`;
  const quoteSection = quoteHtml
    ? `<div data-compose-quote>${quoteHtml}</div>`
    : '';
  return userArea + quoteSection;
}

export {
  buildReplyOpener,
  extractFirstName,
  extractSignatureFirstName,
  detectPleasantry,
  matchCustomOpener,
  detectMateSalutation,
  isReplySubject,
  hasQuotedHistory,
  isOngoingThread,
  // Exported for unit tests / introspection:
  stripHtmlForScan,
  GENERIC_LOCAL_PARTS,
  PLEASANTRY_PATTERNS,
  SALUTATION_OVERRIDES,
  SIGNOFF_PHRASES,
  SIGNATURE_BLOCKLIST,
};
